import { ReactElement, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { getRun } from '../../api/runs';
import { useReprocess } from '../../hooks/useReprocess';
import RunProgressIndicator from '../../components/RunProgressIndicator';
import { BsFileZip } from 'react-icons/bs';
import { HiOutlineDotsVertical } from 'react-icons/hi';
import { HiOutlineArrowPath, HiOutlineMagnifyingGlass } from 'react-icons/hi2';

interface RunActionsProps {
  runId: string;
}

export default function RunActions({ runId }: RunActionsProps): ReactElement {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { handleReprocess, isProcessing, getRunProgress, isRunReprocessing, watchRuns } = useReprocess();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  // Reload recovery: if this run is already mid-reprocess (e.g. the page was
  // refreshed), pick the in-flight state back up by polling /progress.
  useEffect(() => {
    if (runId) watchRuns([runId]);
  }, [runId, watchRuns]);

  const runProgress = getRunProgress(runId);
  const runReprocessing = isRunReprocessing(runId);

  const handleReprocessRun = async (): Promise<void> => {
    try {
      const invalidate = () => {
        queryClient.invalidateQueries({
          queryKey: ['runAndCalibration', runId],
        });
      };
      await handleReprocess(invalidate, [{ id: runId } as any]);
    } catch (error) {
      console.error('Error reprocessing run:', error);
    }
    setIsMenuOpen(false);
  };

  const handleDownloadRawZip = async (): Promise<void> => {
    try {
      // Get the S3 download URL from the API
      const response = await getRun(runId, 'scope=download_raw_zip');

      // The response should contain the S3 URL - navigate to it
      if (response) {
        // 1. Create a temporary anchor element
        const link = document.createElement('a');
        link.href = response;

        // 2. Suggest a filename for the download (optional but recommended)
        // This extracts the filename from the S3 URL before the '?'
        link.setAttribute('download', response.split('?')[0].split('/').pop());

        // 3. Append to the document, click it, and then remove it
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      // if (response.data) {
      //   window.location.href = response.data;
      // }
    } catch (error) {
      console.error('Error getting download URL:', error);
    }
    setIsMenuOpen(false);
  };

  return (
    <div className="cr-relative cr-flex cr-items-center cr-gap-2">
      <RunProgressIndicator info={runProgress} variant="full" />
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className="cr-rounded-lg cr-p-2 cr-text-gray-600 cr-transition-colors hover:cr-bg-gray-100"
        aria-label="More actions"
      >
        <HiOutlineDotsVertical className="cr-h-5 cr-w-5" />
      </button>

      {isMenuOpen && (
        <>
          <div className="cr-fixed cr-inset-0 cr-z-10" onClick={() => setIsMenuOpen(false)} />
          <div className="cr-absolute cr-right-0 cr-z-20 cr-mt-2 cr-w-56 cr-rounded-md cr-bg-white cr-shadow-lg cr-ring-1 cr-ring-black cr-ring-opacity-5">
            <div className="cr-py-1">
              <button
                onClick={handleReprocessRun}
                disabled={isProcessing || runReprocessing}
                className="cr-flex cr-w-full cr-items-center cr-gap-3 cr-px-4 cr-py-2 cr-text-sm cr-text-gray-700 hover:cr-bg-gray-100 disabled:cr-opacity-50 disabled:cr-cursor-not-allowed"
              >
                <HiOutlineArrowPath className={`cr-h-5 cr-w-5 cr-text-blue-600 ${runReprocessing ? 'cr-animate-spin' : ''}`} />
                <span>
                  {runReprocessing ? 'Reprocessing...' : 'Reprocess Run'}
                </span>
              </button>
              <button
                onClick={handleDownloadRawZip}
                className="cr-flex cr-w-full cr-items-center cr-gap-3 cr-px-4 cr-py-2 cr-text-sm cr-text-gray-700 hover:cr-bg-gray-100"
              >
                <BsFileZip className="cr-h-5 cr-w-5 cr-text-blue-600" />
                <span>Download Raw ZIP</span>
              </button>
              <button
                onClick={() => {
                  // Strip current tab from path, append /diagnostic
                  const pathParts = location.pathname.split('/');
                  const runIndex = pathParts.findIndex((p) => p === 'runs');
                  const basePath = pathParts.slice(0, runIndex + 2).join('/');
                  navigate(`${basePath}/diagnostic`);
                  setIsMenuOpen(false);
                }}
                className="cr-flex cr-w-full cr-items-center cr-gap-3 cr-px-4 cr-py-2 cr-text-sm cr-text-gray-700 hover:cr-bg-gray-100"
              >
                <HiOutlineMagnifyingGlass className="cr-h-5 cr-w-5 cr-text-blue-600" />
                <span>Run Diagnostic</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
